import type { ReactNode } from 'react';
import { ArrowLeft, Menu } from 'lucide-react';
import { IconButton, LEGACY_CARD_SHADOW, MoreMenu } from '@components/rep';
import type { MoreMenuItem } from '@components/rep';
import { useAppDispatch } from '@store/hooks';
import { SET_NAV_OPEN } from '@store/app/app.reducer';
import { cn } from '@utils/index';

/**
 * The header buttons (Figma 2782:3977): a 36px square with 8px corners, translucent white on the
 * blue band, around a 16px icon in ui/icon/accent-subtle.
 */
const HEADER_BUTTON_CLASS =
  'rep:size-9! rep:rounded-lg! rep:bg-white/8! rep:hover:bg-white/16! rep:[&_svg]:size-4! rep:[&_svg]:text-[#F0F3FF]!';

interface ListPageShellProps {
  title: string;
  /** The line under the title, e.g. the row count. */
  subtitle?: string;
  /** The header's menu, top right; none when empty. */
  menuItems?: MoreMenuItem[];
  /**
   * A Back arrow in place of the navigation button, for a list opened from another list (a
   * county's letters, opened from the county list).
   */
  onBack?: () => void;
  /** Phone gutters (16px) in place of the desktop ones (24px). */
  isMobile?: boolean;
  /** The white card's content: the toolbar and the table. */
  children: ReactNode;
}

/**
 * List page chrome in the Palmetto 3.0 layout (Figma Virtual EOC 2782:3971), the list-page
 * sibling of EntryPageShell: the blue header (menu button, title and subtitle, optional menu)
 * and the white card that overlaps its bottom 47px, on the page's grey. The whole page scrolls,
 * so the header goes away with the content.
 */
const ListPageShell = ({
  title,
  subtitle,
  menuItems = [],
  onBack,
  isMobile,
  children,
}: ListPageShellProps) => {
  const dispatch = useAppDispatch();
  const gutter = isMobile ? 'rep:px-4' : 'rep:px-6';
  return (
    <div className='rep:h-full rep:w-full rep:overflow-y-auto rep:bg-[#ECEEF2]'>
      <header
        className={cn(
          'rep:flex rep:flex-col rep:items-center rep:gap-6 rep:bg-[#0949EE] rep:bg-linear-to-b rep:from-transparent rep:to-black/20 rep:pb-18',
          gutter,
        )}>
        <div className='rep:flex rep:w-full rep:items-center rep:justify-between rep:py-4'>
          {onBack ? (
            <IconButton aria-label='Back' onClick={onBack} className={HEADER_BUTTON_CLASS}>
              <ArrowLeft className='rep:size-4' />
            </IconButton>
          ) : (
            <IconButton
              aria-label='Open navigation'
              onClick={() => dispatch(SET_NAV_OPEN(true))}
              className={HEADER_BUTTON_CLASS}>
              <Menu className='rep:size-4' />
            </IconButton>
          )}
          <MoreMenu items={menuItems} buttonClassName={HEADER_BUTTON_CLASS} />
        </div>
        <div className='rep:flex rep:w-full rep:max-w-7xl rep:flex-col rep:gap-2'>
          <h1 className='rep:text-[36px] rep:leading-[1.3] rep:font-medium rep:text-[#FEFEFE]'>
            {title}
          </h1>
          {subtitle !== undefined ? (
            <div className='rep:min-h-[1.3em] rep:text-base rep:leading-[1.3] rep:text-[#ECEEF2]'>
              {subtitle}
            </div>
          ) : null}
        </div>
      </header>
      {/* Bottom room for the page's FAB, so it never covers the card's last line. */}
      <div className={cn('rep:pb-28', gutter)}>
        <section
          className='rep:relative rep:mx-auto rep:-mt-11.75 rep:flex rep:w-full rep:max-w-7xl rep:flex-col rep:gap-4 rep:rounded-[14px] rep:bg-white rep:p-4'
          style={{ boxShadow: LEGACY_CARD_SHADOW }}>
          {children}
        </section>
      </div>
    </div>
  );
};

export default ListPageShell;
